import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CompanionAppShell from "@/components/daily/CompanionAppShell";
import CompanionProfilePageV3 from "@/components/daily/CompanionProfilePageV3";
import CompanionCheckInPage from "@/components/daily/CompanionCheckInPage";
import CompanionPlanPageV2 from "@/components/daily/CompanionPlanPageV2";
import CompanionProgressPageV2 from "@/components/daily/CompanionProgressPageV2";
import CompanionLearnPage from "@/components/daily/CompanionLearnPage";
import CompanionAiPage from "@/components/daily/CompanionAiPage";
import CompanionAssessmentSummaryV2 from "@/components/daily/CompanionAssessmentSummaryV2";
import useAdaptiveCompanionInstall from "@/hooks/useAdaptiveCompanionInstall";
import { clearProfileLocal } from "@/components/assessment/assessmentScoring";
import { getEvidenceMapping } from "@/components/assessment/evidenceMapping";
import { buildImplementationPlan } from "@/components/assessment/implementationPlan";
import { computeProgressModel } from "@/components/daily/adaptiveScore";
import {
  assessmentCalendarEvents,
  buildScheduledActivityGuidance,
  expandCalendarEvents,
  loadUserCalendarEvents,
  upcomingCalendarEvents,
} from "@/components/daily/companionCalendarV2";
import { addCompanionWorkoutDraft } from "@/components/daily/companionWorkoutCalendar";
import {
  completeRecord,
  getParticipationSummary,
  loadDailyLog,
  rematchDailyRecommendation,
  saveDailyLog,
} from "@/components/daily/dailyState";
import {
  buildResilientCurrentMatch,
  makeResilientCheckInRecord,
} from "@/components/daily/resilientCurrentMatch";
import { applyImmediateSupportMode } from "@/components/daily/stateMatchedSupport";
import {
  buildCompanionPriorities,
  COMPANION_APP_PAGES,
  getCompanionPageFromPath,
  getRelevantCompanionTopics,
} from "@/components/daily/companionAppKnowledge";

const inferredConditioning = (assessment) => {
  const explicit = assessment?.answers?.current?.conditioningLevel;
  if (["beginner", "general", "advanced", "athlete"].includes(explicit)) return explicit;
  const capacity = assessment?.answers?.capacity || {};
  const values = [capacity.activity, capacity.confidence, capacity.tolerance]
    .map(Number)
    .filter(Number.isFinite);
  if (!values.length) return "general";
  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  if (average <= 2) return "beginner";
  if (average >= 4.5) return "athlete";
  if (average >= 3.7) return "advanced";
  return "general";
};

const initialCheckIn = (assessment) => ({
  level: 3,
  primaryState: "",
  secondaryState: "",
  intensity: 3,
  energy: 3,
  actionPressure: 3,
  behavioralChoice: 3,
  somaticResponse: 3,
  cognitiveCapture: 3,
  recoveryDifficulty: 3,
  environment: "inside",
  minutes: 15,
  physicalReadiness: "",
  supportMode: "auto",
  conditioningLevel: inferredConditioning(assessment),
  hydration: "",
  nourishment: "",
  urge: "none",
});

const planRecord = (record, nextPlan, checkIn) => ({
  ...record,
  reportedStates: nextPlan.currentState.labels,
  system: nextPlan.currentState.system,
  physiologicalTarget: nextPlan.currentState.target,
  stateMatch: nextPlan.stateMatch || record.stateMatch || null,
  supportMode: checkIn?.supportMode || record.supportMode || "auto",
  recommendation: {
    title: nextPlan.immediate.title,
    activityKey: nextPlan.immediate.activityKey || null,
    activityType: nextPlan.immediate.activityType || null,
    duration: nextPlan.immediate.duration,
    instruction: nextPlan.immediate.instruction,
    fallbackLevel: nextPlan.immediate.fallbackLevel,
    breathingProtocol: nextPlan.immediate.breathingProtocol || null,
  },
  preparation: nextPlan.preparation,
  cooldown: nextPlan.cooldown,
  routines: nextPlan.routines,
  alternatives: (nextPlan.week?.recommendations || []).slice(0, 5),
});

const scrollSafely = (options = { top: 0, behavior: "auto" }) => {
  try {
    window.scrollTo(options);
  } catch (_error) {
    try {
      window.scrollTo(0, Number(options.top) || 0);
    } catch (_ignored) {
      // Scrolling support varies across installed mobile browsers.
    }
  }
};

const DailyCompanionExperienceV3 = ({ assessment }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const install = useAdaptiveCompanionInstall();
  const activePage = getCompanionPageFromPath(location.pathname);
  const assessmentView = location.pathname === "/app/assessment-view";
  const activeTopicKey = location.pathname.startsWith("/app/learn/")
    ? decodeURIComponent(location.pathname.replace("/app/learn/", "").split("/")[0])
    : null;

  const [checkIn, setCheckIn] = useState(() => initialCheckIn(assessment));
  const [biometrics, setBiometrics] = useState(null);
  const [entries, setEntries] = useState(() => loadDailyLog());
  const [userCalendarEvents, setUserCalendarEvents] = useState(() => loadUserCalendarEvents());
  const [activeRecord, setActiveRecord] = useState(null);
  const [plan, setPlan] = useState(null);
  const [breathingProtocol, setBreathingProtocol] = useState(null);
  const [responseMagnitude, setResponseMagnitude] = useState(1);
  const [feltWorse, setFeltWorse] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const summary = useMemo(() => getParticipationSummary(entries), [entries]);
  const progress = useMemo(
    () => computeProgressModel({ assessment, entries }),
    [assessment, entries],
  );
  const baselinePlan = useMemo(
    () =>
      buildImplementationPlan({
        profile: assessment.profile,
        mapping: getEvidenceMapping(assessment.profile.key),
        answers: assessment.answers,
      }),
    [assessment],
  );
  const calendarEvents = useMemo(
    () => [
      ...assessmentCalendarEvents({ assessment, baselinePlan }),
      ...userCalendarEvents,
    ],
    [assessment, baselinePlan, userCalendarEvents],
  );
  const expandedCalendarEvents = useMemo(() => {
    const current = new Date();
    const next = new Date(current.getFullYear(), current.getMonth() + 1, 1);
    return [
      ...expandCalendarEvents(calendarEvents, current),
      ...expandCalendarEvents(calendarEvents, next),
    ];
  }, [calendarEvents]);
  const stateMatchingCalendarEvents = useMemo(
    () => expandedCalendarEvents.filter((event) => event.source !== "assessment-plan"),
    [expandedCalendarEvents],
  );
  const upcomingEvents = useMemo(
    () => upcomingCalendarEvents(expandedCalendarEvents, new Date(), 10),
    [expandedCalendarEvents],
  );
  const nextEvent = upcomingEvents[0] || null;
  const nextEventGuidance = useMemo(
    () =>
      nextEvent
        ? buildScheduledActivityGuidance({
            event: nextEvent,
            plan,
            checkIn,
            identity: assessment.identity,
          })
        : null,
    [assessment.identity, checkIn, nextEvent, plan],
  );
  const relevantTopics = useMemo(
    () => getRelevantCompanionTopics({ assessment, plan, biometrics }),
    [assessment, plan, biometrics],
  );
  const priorities = useMemo(
    () => buildCompanionPriorities({ assessment, plan, summary, entries }),
    [assessment, plan, summary, entries],
  );

  const setField = (key, value) => {
    if (key === "supportMode" && value !== "breathing") setBreathingProtocol(null);
    setCheckIn((current) => ({ ...current, [key]: value }));
  };

  const onNavigate = (pageKey, detail) => {
    const page = COMPANION_APP_PAGES.find((item) => item.key === pageKey);
    if (!page) return;
    const path =
      pageKey === "learn" && detail
        ? `/app/learn/${encodeURIComponent(detail)}`
        : page.path;
    navigate(path);
    scrollSafely({ top: 0, behavior: "auto" });
  };

  const openCalendar = () => {
    navigate("/app/plan");
    window.setTimeout(() => {
      document.getElementById("companion-calendar")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  const addWorkoutToCalendar = async (draft) => {
    const result = addCompanionWorkoutDraft({ draft, events: userCalendarEvents });
    setUserCalendarEvents(result.events);
    return result;
  };

  const requiredComplete = Boolean(
    checkIn.primaryState &&
      checkIn.physicalReadiness &&
      checkIn.supportMode &&
      checkIn.hydration &&
      checkIn.nourishment,
  );

  const createRecommendation = () => {
    if (!requiredComplete) {
      setValidationMessage(
        "Select the current nervous-system state, physical readiness, preferred support, hydration, and nourishment before matching the output.",
      );
      return;
    }

    setValidationMessage("");

    try {
      const resolved = buildResilientCurrentMatch({
        assessment,
        checkIn,
        calendarEvents: stateMatchingCalendarEvents,
        entries,
      });
      const nextPlan = resolved.plan;

      if (!nextPlan?.immediate || !nextPlan?.currentState) {
        setValidationMessage(
          "APOS could not create a match from this check-in. Review the required selections and try again.",
        );
        return;
      }

      const baseRecord = makeResilientCheckInRecord({
        checkIn,
        plan: nextPlan,
        biometrics,
        assessmentCompletedAt: assessment.completedAt,
      });
      const record = {
        ...baseRecord,
        supportMode: checkIn.supportMode,
        recoveredFromMatchError: Boolean(resolved.recovered),
        recommendation: {
          ...baseRecord.recommendation,
          breathingProtocol: nextPlan.immediate.breathingProtocol || null,
        },
      };
      const nextEntries = [record, ...entries];

      setPlan(nextPlan);
      setActiveRecord(record);
      setBreathingProtocol(nextPlan.immediate.breathingProtocol || null);
      setResponseMagnitude(1);
      setFeltWorse(false);
      setEntries(nextEntries);
      saveDailyLog(nextEntries);

      if (nextPlan.immediate.breathingProtocol) {
        const revealBreathingSession = () => {
          const target = document.getElementById("state-matched-breathing-session");
          if (target?.scrollIntoView) {
            try {
              target.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            } catch (_error) {
              // Use the mobile-safe fallback below.
            }
          }
          scrollSafely({ top: 0, behavior: "smooth" });
        };

        window.setTimeout(revealBreathingSession, 60);
        window.setTimeout(revealBreathingSession, 240);
        return;
      }

      navigate("/app/plan");
      window.setTimeout(() => scrollSafely({ top: 0, behavior: "auto" }), 0);
    } catch (error) {
      console.error("APOS resilient current check-in matching failed", error);
      setValidationMessage(
        `APOS could not open the matched output (${String(error?.message || "unknown error").slice(0, 120)}). Your check-in was not replaced.`,
      );
    }
  };

  const rematch = async ({ fallbackLevel, minutes }) => {
    if (!plan || !activeRecord) return null;
    const nextCheckIn = minutes ? { ...checkIn, minutes } : checkIn;
    if (minutes) setCheckIn(nextCheckIn);

    const calculatedPlan = rematchDailyRecommendation({
      assessment,
      checkIn: nextCheckIn,
      fallbackLevel,
      calendarEvents: stateMatchingCalendarEvents,
      entries,
    });
    const nextPlan = applyImmediateSupportMode(calculatedPlan, nextCheckIn);
    if (!nextPlan) return null;

    const nextRecord = planRecord(activeRecord, nextPlan, nextCheckIn);
    const nextEntries = entries.map((entry) =>
      entry.id === activeRecord.id ? nextRecord : entry,
    );
    setPlan(nextPlan);
    setActiveRecord(nextRecord);
    setBreathingProtocol(nextPlan.immediate.breathingProtocol || null);
    setEntries(nextEntries);
    saveDailyLog(nextEntries);
    return nextPlan;
  };

  const completeIntervention = () => {
    if (!activeRecord) return;
    const nextEntries = completeRecord(entries, activeRecord.id, {
      responseMagnitude,
      feltWorse,
    });
    setEntries(nextEntries);
    saveDailyLog(nextEntries);
    setActiveRecord(nextEntries.find((entry) => entry.id === activeRecord.id));
  };

  const finishBreathing = () => {
    setBreathingProtocol(null);
    navigate("/app/plan");
    scrollSafely({ top: 0, behavior: "auto" });
  };

  const retakeBaseline = () => {
    clearProfileLocal();
    navigate("/app/assessment");
    scrollSafely({ top: 0, behavior: "auto" });
  };

  let content;
  if (assessmentView) {
    content = (
      <CompanionAssessmentSummaryV2
        assessment={assessment}
        onBack={() => navigate("/app")}
        onRetake={retakeBaseline}
      />
    );
  } else if (activePage === "checkIn") {
    content = (
      <CompanionCheckInPage
        preferredName={assessment.identity?.preferredName}
        checkIn={checkIn}
        setField={setField}
        biometrics={biometrics}
        onBiometrics={setBiometrics}
        summary={summary}
        validationMessage={validationMessage}
        onCreate={createRecommendation}
        breathingProtocol={breathingProtocol}
        onBreathingFinish={finishBreathing}
      />
    );
  } else if (activePage === "plan") {
    content = (
      <CompanionPlanPageV2
        assessment={assessment}
        identity={assessment.identity}
        checkIn={checkIn}
        plan={plan}
        activeRecord={activeRecord}
        responseMagnitude={responseMagnitude}
        setResponseMagnitude={setResponseMagnitude}
        feltWorse={feltWorse}
        setFeltWorse={setFeltWorse}
        calendarEvents={calendarEvents}
        userCalendarEvents={userCalendarEvents}
        onUserCalendarEventsChange={setUserCalendarEvents}
        onAddWorkoutToCalendar={addWorkoutToCalendar}
        onOpenCalendar={openCalendar}
        onRematch={rematch}
        onComplete={completeIntervention}
        onNavigate={onNavigate}
      />
    );
  } else if (activePage === "progress") {
    content = (
      <CompanionProgressPageV2
        progress={progress}
        summary={summary}
        entries={entries}
        onNavigate={onNavigate}
      />
    );
  } else if (activePage === "learn") {
    content = (
      <CompanionLearnPage
        topics={relevantTopics}
        activeTopicKey={activeTopicKey}
        preferredName={assessment.identity?.preferredName}
        onNavigate={onNavigate}
      />
    );
  } else if (activePage === "companion") {
    content = (
      <CompanionAiPage
        assessment={assessment}
        checkIn={checkIn}
        plan={plan}
        entries={entries}
        biometrics={biometrics}
        summary={summary}
        progress={progress}
        upcomingEvents={upcomingEvents}
        nextEventGuidance={nextEventGuidance}
        workoutSourceId={activeRecord?.id || assessment.completedAt}
        onAddWorkoutToCalendar={addWorkoutToCalendar}
        onOpenCalendar={openCalendar}
        onRematch={rematch}
      />
    );
  } else {
    content = (
      <CompanionProfilePageV3
        assessment={assessment}
        plan={plan}
        summary={summary}
        entries={entries}
        biometrics={biometrics}
        progress={progress}
        nextEvent={nextEvent}
        nextEventGuidance={nextEventGuidance}
        priorities={priorities}
        relevantTopics={relevantTopics}
        onNavigate={onNavigate}
        onViewAssessment={() => navigate("/app/assessment-view")}
        onRetake={retakeBaseline}
      />
    );
  }

  return (
    <CompanionAppShell assessment={assessment} install={install}>
      {content}
    </CompanionAppShell>
  );
};

export default DailyCompanionExperienceV3;
